import type { AppDispatch, RootState } from '@store/index';
import type {
  StepId,
  UserInterface,
} from '@store/user/interfaces/userInterfaces';
import { selectUser } from '@store/user/selectors/userSelectors';
import {
  clearErrors,
  resetUser,
  setFieldEditable,
  setFieldReadOnly,
  setFieldsReadOnly,
  updateUser,
  updateUserId,
} from '@store/user/slices/userSlice';
import { validateUser } from '@utils/validations';
import { useDispatch, useSelector } from 'react-redux';

import { getUserByIdThunk } from '../../../services/thunks/userThunks';

// Single hook for all user management
export const useUser = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => selectUser(state));

  const updateUserData = (
    data: Partial<UserInterface>,
    skipValidation = false,
  ) => {
    if (!skipValidation) {
      // Create the updated user object
      const updatedUser = { ...user, ...data };

      // Run validation on the updated user
      const validationErrors = validateUser(updatedUser);

      // Merge validation errors with the data being updated
      const mergedData = {
        ...data,
        errors: validationErrors,
      };

      dispatch(updateUser(mergedData));
    } else {
      // Update without validation
      dispatch(updateUser(data));
    }
  };

  // Fetch user data from API
  const fetchUserById = async (userId: string) => {
    return dispatch(getUserByIdThunk({ userId }));
  };

  const clearAllErrors = () => {
    dispatch(clearErrors());
  };

  const resetUserData = () => {
    dispatch(resetUser());
  };

  // Read-only field management
  const setFieldAsReadOnly = (fieldPath: string) => {
    dispatch(setFieldReadOnly(fieldPath));
  };

  const setFieldAsEditable = (fieldPath: string) => {
    dispatch(setFieldEditable(fieldPath));
  };

  const setMultipleFieldsReadOnly = (fieldPaths: string[]) => {
    dispatch(setFieldsReadOnly(fieldPaths));
  };

  const isFieldReadOnly = (fieldPath: string): boolean => {
    return user.readOnlyFields.includes(fieldPath);
  };

  // Update user ID
  const updateUserIdInStore = (userId: string) => {
    dispatch(updateUserId(userId));
  };

  // Step completion management - now using typed steps
  const completeStep = (stepId: StepId) => {
    const updatedSteps = {
      ...user.steps,
      [stepId]: {
        ...user.steps[stepId],
        isCompleted: true,
        completedAt: new Date().toISOString(),
      },
    };

    // Also update legacy completedSteps array for backward compatibility
    const completedSteps = Array.from(
      new Set([...user.completedSteps, stepId]),
    );

    // Skip validation for step completion updates
    updateUserData({ steps: updatedSteps, completedSteps }, true);
  };

  const isStepCompleted = (stepId: StepId): boolean => {
    return user.steps[stepId]?.isCompleted || false;
  };

  // Additional helper methods for working with typed steps
  const getStep = (stepId: StepId) => {
    return user.steps[stepId];
  };

  const getStepsByOrder = () => {
    return Object.values(user.steps).sort((a, b) => a.order - b.order);
  };

  const getCompletedStepsCount = (): number => {
    return Object.values(user.steps).filter(step => step.isCompleted).length;
  };

  const getTotalStepsCount = (): number => {
    return Object.keys(user.steps).length;
  };

  const getStepUiState = <T = Record<string, unknown>>(stepId: StepId): T => {
    return (user.currentStepData?.[stepId] as T) ?? ({} as T);
  };

  const setStepUiState = <T = Record<string, unknown>>(
    stepId: StepId,
    partial: Partial<T>,
  ) => {
    const prev = (user.currentStepData?.[stepId] as T) ?? ({} as T);
    updateUserData(
      {
        currentStepData: {
          ...user.currentStepData,
          [stepId]: { ...prev, ...partial },
        },
      },
      true, // skipValidation, es solo UI
    );
  };

  return {
    user,
    updateUserData,
    fetchUserById,
    clearAllErrors,
    resetUserData,
    setFieldAsReadOnly,
    setFieldAsEditable,
    setMultipleFieldsReadOnly,
    isFieldReadOnly,
    updateUserIdInStore,
    // Essential step completion methods
    completeStep,
    isStepCompleted,
    // Additional step helper methods
    getStep,
    getStepsByOrder,
    getCompletedStepsCount,
    getTotalStepsCount,
    getStepUiState,
    setStepUiState,
  };
};
